import React, {useEffect, useRef} from 'react';

import {pushSnackAlert, pushSnackInfo} from '@optimaxdev/design-system';
import {
    SnackBarType,
    SnackType,
} from '@optimaxdev/design-system/dist/types/components/snackBar/snackBarType';
import classNames from 'classnames';
import {useSelector} from 'react-redux';

import useStickySidebar from 'desktop/hooks/useStickySidebar/useStickySidebar';
import {flags} from 'libs/rollout';
import {MessageType} from 'types/cartType';

import {PopupDY} from '../../features/popupDY/popupDY';
import {getQuoteItems} from '../../store/selectors';
import {CartMainSection} from '../cartMainSection/cartMainSection';
import {CartSidebarConnected} from '../cartSidebar/cartSidebar';
import {CartSkeleton} from '../cartSkeleton/cartSkeleton';
import {CartTitleConnected} from '../cartTitle/cartTitle';
import {EmptyCart} from '../emptyCart/emptyCart';
import {NextDayShippingBanner} from '../nextDayShippingBanner/nextDayShippingBanner';

import s from './cartContainer.scss';

export type PropsType = {
    showSkeleton: boolean;
    infoMessages: Array<MessageType>;
};

const pushSnackTypes: {
    [key: string]: (
        title: React.ReactNode,
        message: React.ReactNode,
        options?: Omit<SnackType, 'id' | 'type' | 'title' | 'message'>,
    ) => void;
} = {
    alert: pushSnackAlert,
    error: pushSnackAlert,
    info: pushSnackInfo,
    success: pushSnackInfo,
    warning: pushSnackInfo,
};

/**
 * Сart main container
 * @param {PropsType} props - props component
 * @returns {React.ReactElement} - Container component
 */
export const CartContainer: React.FC<PropsType> = ({infoMessages, showSkeleton}) => {
    const sidebarRef = useRef<HTMLDivElement>(null);
    const itemsCount = useSelector(getQuoteItems).length;
    const dCartNextDayImp = flags.dCartNextDayImp.isEnabled();

    useStickySidebar(sidebarRef.current);

    const snackMessagesChangeCount = useRef(0);
    useEffect(() => {
        if (infoMessages.length) {
            infoMessages.forEach(item => {
                // The first visit success messages shouldn't been showed, even when we get it from backend
                if (item.type === 'success' && snackMessagesChangeCount.current <= 1) return;
                pushSnackTypes[item.type](null, item.message, {
                    hasAutoClose: true,
                    iconType: item.type as SnackBarType,
                    variant: 'notification',
                });
            });
        }
        snackMessagesChangeCount.current += 1;
    }, [infoMessages]);

    if (showSkeleton) return <CartSkeleton />;

    const isEmpty = itemsCount === 0;

    return (
        <>
            <div
                className={classNames(s.cartContainer, {
                    [s.cartContainerEmpty]: isEmpty,
                })}
            >
                {!isEmpty && (
                    <>
                        <div className={s.cartHeading}>
                            <CartTitleConnected isIndent={false} />
                        </div>
                        <div className={s.cartHeading}>
                            {!dCartNextDayImp && <NextDayShippingBanner />}
                        </div>
                    </>
                )}

                <div>{isEmpty ? <EmptyCart /> : <CartMainSection />}</div>
                <div className={s.cartSidebar} ref={sidebarRef}>
                    <CartSidebarConnected />
                </div>
            </div>

            <PopupDY contextPageType="CART" />
        </>
    );
};
